import React, { useEffect, useState, useCallback } from 'react'
import Switch from '@mui/material/Switch'
import Box from '@mui/material/Box'
import Pagination from '@mui/material/Pagination'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import NoDataImg from '../../../assets/img/no-data.gif'
import NavBar from '../../structure/NavBar'
import { NavBarTop } from '../../structure/NavBarTop'
import { useDispatch, useSelector } from 'react-redux'
import { showDangerToast, showToast } from '../../constants/toaster'
import { FullImageView } from '../../custom/FullImageView'
import DropBox from '../../custom/DropBox'
import { updateLoading, updateTopHeader } from '../../../redux/features/common/commonSlice'
import urls from '../../constants/apiurl'
import ApiCaller from '../../constants/ApiCaller'
import InsufficientPermission from '../../custom/InSufficientPermission'
import CategoryMappingModal from './CategoryMappingModal'
import { s3 } from '../../constants/awsConfig'

const label = { inputProps: { 'aria-label': 'Size switch demo' } }

// Custom styles for better table appearance
const tableStyles = {
  productName: {
    minWidth: '150px'
  },
  description: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  imageContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
    maxWidth: '120px'
  },
  productImage: {
    width: '40px',
    height: '40px',
    borderRadius: '5px',
    cursor: 'pointer',
    objectFit: 'cover',
    border: '1px solid #ddd',
    transition: 'transform 0.2s'
  },
  actionButtons: {
    minWidth: '140px'
  }
}

const ProductList = () => {
  const [data, setData] = useState([])
  const [filters, setFilters] = useState({
    txt: '',
    merchantId: '',
    active: true,
    startDate: null,
    endDate: null
  })
  const [page, setPage] = useState(1)
  const [pagerecord, setPagerecord] = useState({
    totalItems: 0,
    totalPages: 1,
    pageNumber: 1,
    pageSize: 10
  })
  const [imageUrl, setImageUrl] = useState(false)
  const [fullSizePreview, setFullSizePreview] = useState(false)
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [selectedProductForCategory, setSelectedProductForCategory] = useState(null)

  // Update modal states
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [selectedProductForUpdate, setSelectedProductForUpdate] = useState(null)
  const [categories, setCategories] = useState([])
  const [updateFormData, setUpdateFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    price: '',
    quantity: '',
    salePrice: '',
    images: []
  })
  const [selectedFiles, setSelectedFiles] = useState([])
  const [existingImages, setExistingImages] = useState([])
  const [newImagePreviews, setNewImagePreviews] = useState([])
  const [uploadProgress, setUploadProgress] = useState({ uploading: false, progress: 0, total: 0 })

  const LOADING_ENABLE = useSelector(state => state?.loading)
  const userData = useSelector(state => state?.userData)
  const dispatch = useDispatch()

  // Permission logic - similar to other components
  const userPermissions = useSelector(state => state?.permission)
  let currentModulePermission
  let viewPermission = true
  let editPermission = true
  let deletePermission = true
  let activePermission = true

  if (userPermissions?.modules.length > 0) {
    currentModulePermission = userPermissions.modules.find((element) => {
      if (element.moduleId === '30') { // Assuming moduleId for Product Manager
        return element
      }
    })?.permissions
    if (currentModulePermission) {
      viewPermission = currentModulePermission.find((element) => {
        if (element.name === 'VIEW') {
          return element
        }
      })?.isEnable || false
      editPermission = currentModulePermission.find((element) => {
        if (element.name === 'EDIT') {
          return element
        }
      })?.isEnable || false
      deletePermission = currentModulePermission.find((element) => {
        if (element.name === 'DELETE') {
          return element
        }
      })?.isEnable || false
      activePermission = currentModulePermission.find((element) => {
        if (element.name === 'ACTIVE') {
          return element
        }
      })?.isEnable || false
    }
  }

  useEffect(() => {
    dispatch(updateTopHeader('Product Manager'))
    // Initial load when component mounts
    if (viewPermission) {
      fetchProductList()
    }
  }, [])

  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (showUpdateModal) {
      // Lock body scroll when modal opens
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = '17px' // Compensate for scrollbar width
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset'
      document.body.style.paddingRight = '0px'
    }

    // Cleanup function to restore scroll on component unmount
    return () => {
      document.body.style.overflow = 'unset'
      document.body.style.paddingRight = '0px'
    }
  }, [showUpdateModal])

  const fetchProductList = useCallback(async () => {
    let startDateObject = null
    let endDateObject = null
    if (filters.startDate?.toDate()) {
      let newDate = new Date(filters.startDate?.toDate())
      startDateObject = newDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
    }
    if (filters.endDate?.toDate()) {
      let newDate = new Date(filters.endDate?.toDate())
      endDateObject = newDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
    }

    try {
      dispatch(updateLoading(true))
      const postJson = {
        sort: 'createdAt',
        order: false,
        page: pagerecord?.pageNumber - 1 || 0,
        limit: pagerecord.pageSize || 10,
        active: filters.active,
        merchantId: filters.merchantId?.trim() || '',
        txt: filters.txt?.trim() || '',
        startDate: startDateObject,
        endDate: endDateObject
      }
      let path = urls.productList
      let res = await ApiCaller.post(postJson, path, userData?.access_token)
      const response = await res.response.json()
      if (response.status == 200 || response.status == '200') {
        dispatch(updateLoading(false))
        console.log('Product List Response:', response?.data?.items) // Debug log
        console.log('First item approved status:', response?.data?.items?.[0]?.approved) // Debug approved field
        setData(response?.data?.items || [])
        setPagerecord({
          totalItems: response?.data?.totalItems,
          totalPages: response?.data?.totalPages,
          pageNumber: response?.data?.pageNumber + 1, // Backend returns 0-based, UI uses 1-based
          pageSize: response?.data?.pageSize || 10
        })
      } else {
        dispatch(updateLoading(false))
        showDangerToast(response.message)
      }
    } catch (error) {
      dispatch(updateLoading(false))
      console.error(error)
      showDangerToast(error.message)
    }
  }, [filters, pagerecord, dispatch, userData?.access_token])

  useEffect(() => {
    if (viewPermission) {
      fetchProductList()
    }
  }, [pagerecord?.pageNumber])

  const handleSearch = useCallback(() => {
    setPage(1)
    setPagerecord(prev => ({
      ...prev,
      pageNumber: 1
    }))
    // Trigger search after setting page to 1
    setTimeout(() => {
      fetchProductList()
    }, 100)
  }, [fetchProductList])

  const handleReset = useCallback(() => {
    setFilters({
      txt: '',
      merchantId: '',
      active: true,
      startDate: null,
      endDate: null
    })
    setPage(1)
    setPagerecord(prev => ({
      ...prev,
      pageNumber: 1
    }))
    // Trigger search after resetting filters
    setTimeout(() => {
      fetchProductList()
    }, 100)
  }, [fetchProductList])

  const handleChange = (event, value) => {
    setPage(value)
    setPagerecord({
      totalItems: pagerecord?.totalItems,
      totalPages: pagerecord?.totalPages,
      pageNumber: value,
      pageSize: pagerecord?.pageSize || 10
    })
  }

  const handleStatusChange = async (event, id) => {
    try {
      dispatch(updateLoading(true))
      let path = urls.changeProductStatus + '/' + id
      let res = await ApiCaller.put({}, path, userData?.access_token)
      const response = await res.response.json()
      if (response.status === 200) {
        dispatch(updateLoading(false))
        showToast(response?.message)
        fetchProductList()
      } else {
        dispatch(updateLoading(false))
        showDangerToast(response.message)
      }
    } catch (error) {
      dispatch(updateLoading(false))
      console.error(error)
      showDangerToast(error.message)
    }
  }

  const handleApproveReject = async (id, action) => {
    const actionText = action === 'approve' ? 'approve' : 'reject'
    const confirmMessage = `Are you sure you want to ${actionText} this product?`

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      dispatch(updateLoading(true))
      let path = urls.approveRejectProduct + '/' + id
      const postJson = {
        action: action // 'approve' or 'reject'
      }
      let res = await ApiCaller.put(postJson, path, userData?.access_token)
      const response = await res.response.json()
      if (response.status === 200 || response.status === '200') {
        dispatch(updateLoading(false))
        showToast(response?.message)
        fetchProductList()
      } else {
        dispatch(updateLoading(false))
        showDangerToast(response.message)
      }
    } catch (error) {
      dispatch(updateLoading(false))
      console.error(error)
      showDangerToast(error.message)
    }
  }

  const handleCategoryMapping = (product) => {
    setSelectedProductForCategory(product)
    setShowCategoryModal(true)
  }

  const handleCategoryModalClose = () => {
    setShowCategoryModal(false)
    setSelectedProductForCategory(null)
  }

  const handleCategoryUpdateSuccess = () => {
    fetchProductList() // Refresh the product list after successful category update
  }

  // Fetch categories for dropdown
  const fetchCategories = useCallback(async () => {
    try {
      let path = urls.productCategoryKeyValuePair
      let res = await ApiCaller.get({}, path, userData?.access_token)
      const response = await res.response.json()
      if (response.status === 200 || response.status === '200') {
        // Handle the API response structure: {key: "id", value: "name"}
        setCategories(response?.data || [])
      } else {
        showDangerToast('Failed to fetch categories')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      showDangerToast('Failed to fetch categories')
    }
  }, [userData?.access_token])

  // Handle edit product button click
  const handleEditProduct = (product) => {
    console.log('Product data for editing:', product) // Debug log to see product structure
    
    // Extract category ID from product data
    let categoryId = ''
    if (product.categoryId) {
      categoryId = product.categoryId
    } else if (product.categoryDetails) {
      // If categoryDetails is an object with id property
      if (typeof product.categoryDetails === 'object' && product.categoryDetails.id) {
        categoryId = product.categoryDetails.id
      }
      // If categoryDetails is just a string (category ID)
      else if (typeof product.categoryDetails === 'string') {
        categoryId = product.categoryDetails
      }
    }
    
    setSelectedProductForUpdate(product)
    setUpdateFormData({
      name: product.name || product.itemName || '',
      description: product.description || '',
      categoryId: categoryId,
      price: product.price || '',
      quantity: product.quantity || '',
      salePrice: product.salePrice || '',
      images: product.productImages || []
    })
    setExistingImages(product.productImages || [])
    setSelectedFiles([])
    setNewImagePreviews([])
    setShowUpdateModal(true)
    fetchCategories() // Fetch categories when opening modal
  }

  // Handle update modal close
  const handleUpdateModalClose = () => {
    setShowUpdateModal(false)
    setSelectedProductForUpdate(null)
    setUpdateFormData({
      name: '',
      description: '',
      categoryId: '',
      price: '',
      quantity: '',
      salePrice: '',
      images: []
    })
    setSelectedFiles([])
    setExistingImages([])
    setNewImagePreviews([])
    setUploadProgress({ uploading: false, progress: 0, total: 0 })
  }

  // Handle form input changes
  const handleUpdateFormChange = (field) => (e) => {
    setUpdateFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }))
  }

  // Handle file selection
  const handleFileSelect = (files) => {
    if (existingImages.length + selectedFiles.length + files.length > 5) {
      showDangerToast('Maximum 5 images/videos allowed')
      return
    }

    const newFiles = [...selectedFiles, ...files]
    setSelectedFiles(newFiles)

    // Create previews for new files
    const previews = newFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('image/') ? 'image' : 'video'
    }))
    setNewImagePreviews(previews)
  }

  // Remove existing image
  const removeExistingImage = (index) => {
    const updatedImages = existingImages.filter((_, i) => i !== index)
    setExistingImages(updatedImages)
  }

  // Remove new selected file
  const removeSelectedFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index)
    const updatedPreviews = newImagePreviews.filter((_, i) => i !== index)
    setSelectedFiles(updatedFiles)
    setNewImagePreviews(updatedPreviews)
  }

  // Upload files to S3 using AWS Cognito
  const uploadFilesToS3 = async (files) => {
    try {
      setUploadProgress({ uploading: true, progress: 0, total: files.length })
      
      const uploadPromises = files.map(async (file, index) => {
        return new Promise((resolve, reject) => {
          // Generate unique file name following subscription-package pattern
          const timestamp = new Date().getTime()
          const fileName = `product-images/${timestamp}-${file.name}`

          // S3 upload parameters (following subscription-package pattern)
          const uploadParams = {
            Bucket: 'supa-dev-bucket',
            Key: fileName,
            Body: file,
            ContentType: file.type,
            // ACL: 'public-read', // Removed ACL as per subscription-package pattern
          }

          // Upload file to S3
          const upload = s3.upload(uploadParams)
          
          // Track progress
          upload.on('httpUploadProgress', (progress) => {
            const progressPercent = Math.round((progress.loaded / progress.total) * 100)
            console.log(`File ${index + 1} upload progress: ${progressPercent}%`)
          })

          upload.send((err, data) => {
            if (err) {
              console.error('S3 upload error:', err)
              reject(err)
            } else {
              // Replace S3 URL with CloudFront URL for better performance (following subscription-package pattern)
              const url = data.Location.replace(
                "https://supa-dev-bucket.s3.af-south-1.amazonaws.com/",
                "https://d2up68x544gew2.cloudfront.net/"
              );
              console.log('S3 upload success:', url)
              setUploadProgress(prev => ({ 
                ...prev, 
                progress: prev.progress + 1 
              }))
              resolve(url)
            }
          })
        })
      })

      const results = await Promise.all(uploadPromises)
      setUploadProgress({ uploading: false, progress: 0, total: 0 })
      return results
    } catch (error) {
      console.error('Error uploading files to S3:', error)
      setUploadProgress({ uploading: false, progress: 0, total: 0 })
      throw error
    }
  }

  // Handle product update
  const handleProductUpdate = async () => {
    try {
      // Validate required fields
      if (!updateFormData.name.trim()) {
        showDangerToast('Product name is required')
        return
      }
      if (!updateFormData.categoryId) {
        showDangerToast('Category is required')
        return
      }
      if (!updateFormData.price || parseFloat(updateFormData.price) <= 0) {
        showDangerToast('Valid price is required')
        return
      }
      if (!updateFormData.quantity || parseInt(updateFormData.quantity) < 0) {
        showDangerToast('Valid quantity is required')
        return
      }

      dispatch(updateLoading(true))

      // Upload new files if any
      let newImageUrls = []
      if (selectedFiles.length > 0) {
        try {
          newImageUrls = await uploadFilesToS3(selectedFiles)
        } catch (error) {
          showDangerToast('Failed to upload images')
          dispatch(updateLoading(false))
          return
        }
      }

      // Combine existing images with new uploaded images
      const allImages = [...existingImages, ...newImageUrls]

      // Prepare update data
      const updateData = {
        name: updateFormData.name.trim(),
        description: updateFormData.description.trim(),
        categoryId: updateFormData.categoryId,
        price: parseFloat(updateFormData.price),
        quantity: parseInt(updateFormData.quantity),
        salePrice: parseFloat(updateFormData.salePrice) || 0,
        images: allImages
      }

      // Send update request
      let path = urls.updateProduct + '/' + selectedProductForUpdate.uuid
      let res = await ApiCaller.put(updateData, path, userData?.access_token)
      const response = await res.response.json()

      if (response.status === 200 || response.status === '200') {
        showToast(response.message || 'Product updated successfully')
        handleUpdateModalClose()
        fetchProductList() // Refresh the product list
      } else {
        showDangerToast(response.message || 'Failed to update product')
      }
    } catch (error) {
      console.error('Error updating product:', error)
      showDangerToast('Failed to update product')
    } finally {
      dispatch(updateLoading(false))
    }
  }

  const serialNumber = i => {
    return i + 1 + ((page - 1) * (pagerecord.pageSize || 10))
  }

  // Handle text input changes
  const handleTextChange = useCallback((field) => (e) => {
    setFilters(f => ({ ...f, [field]: e.target.value }))
  }, [])

  // Handle checkbox change
  const handleActiveChange = useCallback((e) => {
    setFilters(f => ({ ...f, active: e.target.checked }))
  }, [])

  // Handle date changes
  const handleStartDateChange = useCallback((newValue) => {
    setFilters(f => ({ ...f, startDate: newValue }))
  }, [])

  const handleEndDateChange = useCallback((newValue) => {
    setFilters(f => ({ ...f, endDate: newValue }))
  }, [])

  // Handle Enter key press for search
  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }, [handleSearch])

  return (
    <>
      <NavBar />
      <div className="main-content">
        <NavBarTop />
        <div className="header">
          <div className="logo">
            <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8"></div>
          </div>
        </div>
        {viewPermission ? (
          <div className="container-fluid mt--7">
            <div className="row">
              <div className="col text-left">
                <div className="card shadow">
                  <div className="card-header border-0">
                    <div className="row align-items-center text-left">
                      <div className="col">
                        <h3 className="mb-0">Product Manager</h3>
                      </div>
                    </div>
                  </div>
                  <div className="card-header border-0" >
                    <div className="row align-items-center">
                      <div className="col-md-3 mb-2">
                        <input
                          className="form-control"
                          placeholder="Search product"
                          value={filters.txt}
                          onChange={handleTextChange('txt')}
                          onKeyPress={handleKeyPress}
                        />
                      </div>
                      <div className="col-md-3 mb-2">
                        <input
                          className="form-control"
                          placeholder="Merchant ID"
                          value={filters.merchantId}
                          onChange={handleTextChange('merchantId')}
                          onKeyPress={handleKeyPress}
                        />
                      </div>
                      <div className="col-md-2 mb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={filters.active}
                            id="activeCheck"
                            onChange={handleActiveChange}
                          />
                          <label className="form-check-label" htmlFor="activeCheck">
                            Active
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <button
                          className="btn btn-primary btn-sm mr-2"
                          onClick={handleSearch}
                          disabled={LOADING_ENABLE}
                        >
                          {LOADING_ENABLE ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                              Searching...
                            </>
                          ) : (
                            'Search'
                          )}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleReset}
                          disabled={LOADING_ENABLE}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                    <div className="row align-items-center">
                      <div className="col-md-6 mb-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DatePicker', 'DatePicker']}>
                            <DatePicker
                              disableFuture
                              className='custom-date'
                              label='From Date'
                              value={filters.startDate}
                              onChange={handleStartDateChange}
                              maxDate={filters.endDate}
                            />
                            <DatePicker
                              disableFuture
                              label='To Date'
                              value={filters.endDate}
                              onChange={handleEndDateChange}
                              minDate={filters.startDate}
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                      </div>
                    </div>
                  </div>
                  <div className='table-responsive'>
                    {data.length ? (
                      <table className='table align-items-center table-flush table-hover'>
                        <thead className='thead-light'>
                          <tr>
                            <th scope='col'>S/No.</th>
                            <th scope='col'>Product Name</th>
                            <th scope='col'>Merchant Name</th>
                            <th scope='col'>Description</th>
                            <th scope='col'>Category</th>
                            <th scope='col'>Price</th>
                            <th scope='col'>Sale Price</th>
                            <th scope='col'>Quantity</th>
                            <th scope='col'>Currency</th>
                            <th scope='col'>Images</th>
                            <th scope='col'>Status</th>
                            <th scope='col'>Approval</th>
                            <th scope='col'>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((item, i) => (
                            <tr key={item.uuid}>
                              <td>{serialNumber(i)}</td>
                              <td style={tableStyles.productName}>
                                <div>
                                  <strong>{item.itemName || '-'}</strong>
                                  {item.itemUrl && (
                                    <div className="small text-muted">
                                      <a href={item.itemUrl} target="_blank" rel="noopener noreferrer">
                                        View Item
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>{item?.merchantBusinessResponse?.businessName || '-'}</td>
                              <td>
                                <div style={tableStyles.description}>
                                  {item.description ? (
                                    <span title={item.description}>
                                      {item.description.length > 50
                                        ? `${item.description.substring(0, 50)}...`
                                        : item.description
                                      }
                                    </span>
                                  ) : '-'}
                                </div>
                              </td>
                              <td>
                                <div>
                                  {/* <span className="badge badge-primary">
                                    {typeof item.category === 'object' ? item.category?.name || '-' : item.category || '-'}
                                  </span> */}
                                  {item.categoryDetails && (
                                    <div className="small text-muted mt-1">
                                      {typeof item.categoryDetails === 'object' ? item.categoryDetails?.name || item.categoryDetails?.id || JSON.stringify(item.categoryDetails) : item.categoryDetails}
                                    </div>
                                  )}
                                  {item.subCategoryDetails && (
                                    <div className="small text-muted">
                                      Sub: {typeof item.subCategoryDetails === 'object' ? item.subCategoryDetails?.name || item.subCategoryDetails?.id || JSON.stringify(item.subCategoryDetails) : item.subCategoryDetails}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <strong>{item.currencyCode || ''}{item.price || '0'}</strong>
                              </td>
                              <td>
                                <span className="text-success">
                                  <strong>{item.currencyCode || ''}{item.salePrice || '0'}</strong>
                                </span>
                              </td>
                              <td>
                                <div>
                                  <span className="badge badge-primary">{item.quantity || '0'}</span>
                                  {item.userCartQty > 0 && (
                                    <div className="small text-muted">
                                      Cart: {item.userCartQty}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="badge badge-primary">
                                  {item.currencyCode || 'N/A'}
                                </span>
                              </td>
                              <td>
                                <div style={tableStyles.imageContainer}>
                                  {item.productImages && item.productImages.length > 0 ? (
                                    item.productImages.map((img, idx) => (
                                      <img
                                        key={idx}
                                        src={img}
                                        style={tableStyles.productImage}
                                        onClick={() => {
                                          setFullSizePreview(true)
                                          setImageUrl(img)
                                        }}
                                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                        alt="product"
                                        title="Click to view full size"
                                      />
                                    ))
                                  ) : (
                                    <span className="text-muted small">No images</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Switch
                                  {...label}
                                  checked={item.active}
                                  disabled={!activePermission}
                                  onChange={e => handleStatusChange(e, item.uuid)}
                                  size='small'
                                />
                                {item.taxDetails && (
                                  <div className="small text-muted mt-1" title="Tax Details">
                                    <i className="fas fa-receipt"></i> Tax
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className={`badge ${item.approved ? 'badge-success' : 'badge-warning'}`}>
                                  {item.approved ? 'Approved' : 'Pending'}
                                </span>
                              </td>
                              <td style={tableStyles.actionButtons}>
                                <div className="d-flex gap-1 flex-wrap">
                                  {/* Edit Button */}
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleEditProduct(item)}
                                    disabled={LOADING_ENABLE || !editPermission}
                                    title="Edit Product"
                                  >
                                    <i className="fas fa-edit"></i> Edit
                                  </button>

                                  {item.approved ? (
                                    // If product is approved, show reject button
                                    <button
                                      className="btn btn-danger btn-sm"
                                      onClick={() => handleApproveReject(item.uuid, 'reject')}
                                      disabled={LOADING_ENABLE || !editPermission}
                                      title="Reject Product"
                                    >
                                      <i className="fas fa-times"></i> Reject
                                    </button>
                                  ) : (
                                    // If product is not approved, show approve button
                                    <button
                                      className="btn btn-success btn-sm"
                                      onClick={() => handleApproveReject(item.uuid, 'approve')}
                                      disabled={LOADING_ENABLE || !editPermission}
                                      title="Approve Product"
                                    >
                                      <i className="fas fa-check"></i> Approve
                                    </button>
                                  )}

                                  {/* Category Mapping Button */}
                                  <button
                                    className="btn btn-info btn-sm"
                                    onClick={() => handleCategoryMapping(item)}
                                    disabled={LOADING_ENABLE || !editPermission}
                                    title="Map Category"
                                  >
                                    <i className="fas fa-tags"></i> Category
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className='text-center'>
                        <img width={'25%'} src={NoDataImg} alt="No Data" />
                        <br />
                        <label style={{ fontWeight: 'bold' }}>
                          {LOADING_ENABLE ? (
                            <>Loading Data.....</>
                          ) : (
                            <>No Data Found</>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                  {data.length > 0 && (
                    <div className='card-footer py-4'>
                      <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <div>
                          <span>Total record: {pagerecord.totalItems}</span>
                          <span className="ml-3 text-muted">
                            Page {pagerecord.pageNumber} of {pagerecord.totalPages}
                          </span>
                        </div>
                        <Pagination
                          className='custom-pagination-class'
                          count={pagerecord.totalPages}
                          page={page}
                          onChange={handleChange}
                          color='secondary'
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    </div>
                  )}
                  <FullImageView
                    src={imageUrl}
                    show={fullSizePreview}
                    onHide={() => setFullSizePreview(false)}
                  />

                  {/* Product Update Modal */}
                  {showUpdateModal && (
                    <div 
                      className="modal fade show" 
                      style={{ 
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1050,
                        overflow: 'auto'
                      }}
                      onClick={(e) => {
                        // Close modal when clicking on backdrop
                        if (e.target === e.currentTarget) {
                          handleUpdateModalClose()
                        }
                      }}
                    >
                      <div 
                        className="modal-dialog modal-lg" 
                        style={{
                          margin: '20px auto',
                          maxWidth: '800px',
                          position: 'relative'
                        }}
                      >
                        <div 
                          className="modal-content"
                          style={{
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <div className="modal-header" style={{ flexShrink: 0 }}>
                            <h5 className="modal-title">Update Product</h5>
                            <button
                              type="button"
                              className="close"
                              onClick={handleUpdateModalClose}
                              aria-label="Close"
                            >
                              <span aria-hidden="true">&times;</span>
                            </button>
                          </div>
                          <div 
                            className="modal-body" 
                            style={{
                              maxHeight: 'calc(90vh - 120px)',
                              overflowY: 'auto',
                              flex: 1
                            }}
                          >
                            <form>
                              <div className="row">
                                {/* Product Name */}
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="productName" className="form-label">
                                    Product Name *
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="productName"
                                    value={updateFormData.name}
                                    onChange={handleUpdateFormChange('name')}
                                    required
                                  />
                                </div>

                                {/* Category */}
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="category" className="form-label">
                                    Category *
                                  </label>
                                  <select
                                    className="form-control"
                                    id="category"
                                    value={updateFormData.categoryId}
                                    onChange={handleUpdateFormChange('categoryId')}
                                    required
                                  >
                                    <option value="">Select Category</option>
                                    {categories.map((category) => (
                                      <option key={category.key} value={category.key}>
                                        {category.value}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Price */}
                                <div className="col-md-4 mb-3">
                                  <label htmlFor="price" className="form-label">
                                    Price *
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    id="price"
                                    value={updateFormData.price}
                                    onChange={handleUpdateFormChange('price')}
                                    min="0"
                                    step="0.01"
                                    required
                                  />
                                </div>

                                {/* Sale Price */}
                                <div className="col-md-4 mb-3">
                                  <label htmlFor="salePrice" className="form-label">
                                    Sale Price
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    id="salePrice"
                                    value={updateFormData.salePrice}
                                    onChange={handleUpdateFormChange('salePrice')}
                                    min="0"
                                    step="0.01"
                                  />
                                </div>

                                {/* Quantity */}
                                <div className="col-md-4 mb-3">
                                  <label htmlFor="quantity" className="form-label">
                                    Quantity *
                                  </label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    id="quantity"
                                    value={updateFormData.quantity}
                                    onChange={handleUpdateFormChange('quantity')}
                                    min="0"
                                    required
                                  />
                                </div>

                                {/* Description */}
                                <div className="col-12 mb-3">
                                  <label htmlFor="description" className="form-label">
                                    Description
                                  </label>
                                  <textarea
                                    className="form-control"
                                    id="description"
                                    rows="3"
                                    value={updateFormData.description}
                                    onChange={handleUpdateFormChange('description')}
                                  />
                                </div>

                                {/* Images Section */}
                                <div className="col-12 mb-3">
                                  <label className="form-label">Images/Videos (Max 5)</label>

                                  {/* Existing Images */}
                                  {existingImages.length > 0 && (
                                    <div className="mb-3">
                                      <h6>Current Images:</h6>
                                      <div className="d-flex flex-wrap gap-2">
                                        {existingImages.map((image, index) => (
                                          <div key={index} className="position-relative">
                                            <img
                                              src={image}
                                              alt="Existing"
                                              style={{
                                                width: '80px',
                                                height: '80px',
                                                objectFit: 'cover',
                                                borderRadius: '5px',
                                                border: '1px solid #ddd'
                                              }}
                                            />
                                            <button
                                              type="button"
                                              className="btn btn-danger btn-sm position-absolute"
                                              style={{ top: '-5px', right: '-5px', width: '20px', height: '20px', borderRadius: '50%', padding: '0', fontSize: '12px' }}
                                              onClick={() => removeExistingImage(index)}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* New Image Selection */}
                                  {existingImages.length + selectedFiles.length < 5 && (
                                    <div className="mb-3">
                                      <h6>Add New Images:</h6>
                                      <input
                                        type="file"
                                        className="form-control"
                                        multiple
                                        accept="image/*,video/*"
                                        onChange={(e) => handleFileSelect(Array.from(e.target.files))}
                                      />
                                    </div>
                                  )}

                                  {/* New Image Previews */}
                                  {newImagePreviews.length > 0 && (
                                    <div className="mb-3">
                                      <h6>New Images Preview:</h6>
                                      <div className="d-flex flex-wrap gap-2">
                                        {newImagePreviews.map((preview, index) => (
                                          <div key={index} className="position-relative">
                                            {preview.type === 'image' ? (
                                              <img
                                                src={preview.preview}
                                                alt="Preview"
                                                style={{
                                                  width: '80px',
                                                  height: '80px',
                                                  objectFit: 'cover',
                                                  borderRadius: '5px',
                                                  border: '1px solid #ddd'
                                                }}
                                              />
                                            ) : (
                                              <video
                                                src={preview.preview}
                                                style={{
                                                  width: '80px',
                                                  height: '80px',
                                                  objectFit: 'cover',
                                                  borderRadius: '5px',
                                                  border: '1px solid #ddd'
                                                }}
                                              />
                                            )}
                                            <button
                                              type="button"
                                              className="btn btn-danger btn-sm position-absolute"
                                              style={{ top: '-5px', right: '-5px', width: '20px', height: '20px', borderRadius: '50%', padding: '0', fontSize: '12px' }}
                                              onClick={() => removeSelectedFile(index)}
                                            >
                                              ×
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <small className="text-muted">
                                    Total images: {existingImages.length + selectedFiles.length}/5
                                  </small>
                                </div>
                              </div>
                            </form>
                          </div>
                          <div 
                            className="modal-footer" 
                            style={{ 
                              flexShrink: 0,
                              borderTop: '1px solid #dee2e6',
                              padding: '15px'
                            }}
                          >
                            {/* Upload Progress Display */}
                            {uploadProgress.uploading && (
                              <div className="flex-grow-1 mr-3">
                                <small className="text-muted d-block">
                                  Uploading images: {uploadProgress.progress}/{uploadProgress.total}
                                </small>
                                <div className="progress" style={{ height: '6px' }}>
                                  <div 
                                    className="progress-bar bg-info" 
                                    style={{ 
                                      width: `${(uploadProgress.progress / uploadProgress.total) * 100}%`,
                                      transition: 'width 0.3s ease'
                                    }}
                                  ></div>
                                </div>
                              </div>
                            )}
                            
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={handleUpdateModalClose}
                              disabled={LOADING_ENABLE || uploadProgress.uploading}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleProductUpdate}
                              disabled={LOADING_ENABLE || uploadProgress.uploading}
                            >
                              {LOADING_ENABLE ? (
                                <>
                                  <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                  Updating...
                                </>
                              ) : (
                                'Update Product'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Category Mapping Modal */}
                  <CategoryMappingModal
                    show={showCategoryModal}
                    handleClose={handleCategoryModalClose}
                    productData={selectedProductForCategory}
                    onSuccess={handleCategoryUpdateSuccess}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <InsufficientPermission />
        )}
      </div>
    </>
  )
}

export default ProductList